import classNames from "classnames";
import { ESharedFunctionMultiple } from "motw_core/Enums/ESharedFunction";
import * as React from "react";
import { IRootNodeFormState } from "source/app/presentation/MainPanel/Workflows/RootNodeName/Helpers/IRootNodeFormState";
import {
    QuickSaveNodeChangesAsync,
    SaveNodeChangesAsync,
} from "source/app/presentation/MainPanel/Workflows/RootNodeName/SaveNodeChangesAsync";
import { useDebounce } from "source/app/services/Shared/Hooks/useDebounce";
import IconWithText from "source/app/services/Shared/IconWithText/IconWithText";
import { ReadStateMultiple } from "source/app/services/UI/State/ReadState";
import { ShareStateMultiple } from "source/app/services/UI/State/ShareState";
import { GotoParentAsync } from "source/app/services/Workflows/GotoNode";
import * as toastr from "toastr";
import { ESaveState } from "./ESaveState";
import { saveStateDetails } from "./saveStateDetails";
import { sharedStyleClass } from "./sharedStyleClass";

export function QuickSaveIcon({
    id,
    state,
}: {
    id: string;
    state: IRootNodeFormState;
}) {
    const [saveState, setSaveState] = React.useState<ESaveState>(
        ESaveState.saved
    );

    ShareStateMultiple(
        ESharedFunctionMultiple.QuickSaveIcon_saveState,
        id,
        saveState,
        setSaveState
    );

    React.useEffect(() => {
        setSaveState(ESaveState.unsaved);
    }, [state.newName]);

    const debouncedValue = useDebounce<string>(state.newName, 1000);

    React.useEffect(() => {
        setSaveState(ESaveState.saving);
        console.log(`[Workflows] auto-saving node...`);

        QuickSaveNodeChangesAsync().then(() => {
            setSaveState(ESaveState.saved);
            console.log(`[Workflows] node changes auto-saved`);
        });
    }, [debouncedValue]);

    const saveStateDetail = saveStateDetails.find(
        (ssd) => ssd.saveState === saveState
    )!;

    return (
        <div
            id={id}
            className={classNames(sharedStyleClass, saveStateDetail.styleClass)}
        >
            <IconWithText
                iconSuffix={saveStateDetail.icon}
                whenClicked={async () => TriggerQuickSaveAsync(state, id)}
                displayAsBlock={false}
            />
        </div>
    );
}

export async function TriggerQuickSaveAsync(
    state: IRootNodeFormState,
    quickSaveIconId: string
) {
    const currentState = ReadStateMultiple<ESaveState>(
        ESharedFunctionMultiple.QuickSaveIcon_saveState,
        quickSaveIconId
    );

    if (currentState === undefined) {
        throw new Error(
            `Save state for QuickSaveIcon with id '${quickSaveIconId}' cannot be determined`
        );
    }

    if (currentState === ESaveState.saving) {
        toastr.info(`Still saving; please wait...`);
        return;
    }

    const shouldSkipWriteToDb = currentState === ESaveState.saved;
    await SaveNodeChangesAsync(shouldSkipWriteToDb);

    if (!state.parentNode) {
        console.log(`Whoops! Couldn't determine parent node!`, state);
    }

    if (state.parentNode?.options?.goToParentOnQuickSave) {
        await GotoParentAsync(state.rootNode);
    }
}
